import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Alert,
  Checkbox,
  Slide,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import MapIcon from '@mui/icons-material/Map';
import BoltIcon from '@mui/icons-material/Bolt';
import api, { festivals as festivalsApi } from '../../services/api';
import BulkImportBoothMap from './BulkImportBoothMap';

const MAX_BOOTHS_PER_VENDOR = 3;

const cellNameCompare = (a, b) =>
  String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

const STATUS_OPTIONS = [
  { id: 'approved', label: 'Approved' },
  { id: 'complete', label: 'Complete' },
  { id: 'pending_approval', label: 'Pending' },
  { id: 'denied', label: 'Denied' },
  { id: 'incomplete', label: 'Incomplete' },
];

const DEFAULT_STATUSES = ['approved', 'complete'];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const formatStatusLabel = (status) => {
  if (!status) return '';
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

// Build the initial per-vendor booth assignment map. For each vendor we try
// to use the same booth (by cell name) they had last year. If that booth no
// longer exists or is taken in the current festival, we auto-fill the next
// alphanumerically-sorted available booth so the booth count matches last
// year. Booths claimed by earlier vendors in the loop are skipped so we
// never propose the same booth twice.
const computeInitialAssignments = (vendorsList, currentBooths) => {
  const availableSorted = (currentBooths || [])
    .filter(
      (b) =>
        b.cell_type === 'booth' &&
        b.booth_status === 'available' &&
        !b.is_registered
    )
    .sort((a, b) => cellNameCompare(a.cell_name, b.cell_name));

  const byCellName = new Map(availableSorted.map((b) => [String(b.cell_name), b]));
  const claimed = new Set();
  const result = {};

  for (const vendor of vendorsList || []) {
    const priorBooths = Array.isArray(vendor.last_booths) ? vendor.last_booths : [];
    const assignments = priorBooths.map((prior) => ({
      boothId: null,
      cellName: null,
      source: 'unavailable',
      priorCellName: prior?.cell_name ? String(prior.cell_name) : null,
      needsElectricity: false,
      isCorner: false,
      hasElectricity: false,
      _priorNeedsElectricity: Boolean(prior?.needs_electricity),
    }));

    // Pass 1: claim exact cell_name matches so a later auto-fill never
    // steals a booth the vendor actually had.
    assignments.forEach((slot) => {
      if (!slot.priorCellName) return;
      const exact = byCellName.get(slot.priorCellName);
      if (exact && !claimed.has(exact.id)) {
        claimed.add(exact.id);
        slot.boothId = exact.id;
        slot.cellName = exact.cell_name;
        slot.source = 'prior';
        slot.needsElectricity =
          slot._priorNeedsElectricity && Boolean(exact.has_electricity);
        slot.isCorner = Boolean(exact.is_corner);
        slot.hasElectricity = Boolean(exact.has_electricity);
      }
    });

    // Pass 2: fill remaining slots with the next available booth so the
    // total count matches last year.
    assignments.forEach((slot) => {
      if (slot.boothId) return;
      const fill = availableSorted.find((b) => !claimed.has(b.id));
      if (!fill) return;
      claimed.add(fill.id);
      slot.boothId = fill.id;
      slot.cellName = fill.cell_name;
      slot.source = 'autofill';
      slot.needsElectricity =
        slot._priorNeedsElectricity && Boolean(fill.has_electricity);
      slot.isCorner = Boolean(fill.is_corner);
      slot.hasElectricity = Boolean(fill.has_electricity);
    });

    // Drop the internal scratchpad field before exposing the assignments.
    result[vendor.id] = assignments.map(({ _priorNeedsElectricity, ...rest }) => rest);
  }
  return result;
};

const BulkEnrollReturningVendors = ({ open, onClose }) => {
  const [festivals, setFestivals] = useState([]);
  const [festivalsLoading, setFestivalsLoading] = useState(false);
  const [activeFestivalId, setActiveFestivalId] = useState(null);
  const [sourceFestivalId, setSourceFestivalId] = useState('');
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [vendors, setVendors] = useState([]);
  const [sourceFestival, setSourceFestival] = useState(null);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [currentBooths, setCurrentBooths] = useState([]);
  const [boothsLoading, setBoothsLoading] = useState(false);
  const [vendorAssignments, setVendorAssignments] = useState({});
  const [mapVendorId, setMapVendorId] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resultSummary, setResultSummary] = useState(null);

  // Reset transient state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setStatuses(DEFAULT_STATUSES);
    setSourceFestivalId('');
    setSearch('');
    setSelected(new Set());
    setError(null);
    setResultSummary(null);
    setVendorAssignments({});
    setMapVendorId(null);
  }, [open]);

  // Load festivals and resolve the active festival when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setFestivalsLoading(true);
      try {
        const [list, active] = await Promise.all([
          festivalsApi.list(),
          festivalsApi.getActive().catch(() => null),
        ]);
        if (cancelled) return;
        const sortedList = Array.isArray(list)
          ? [...list].sort((a, b) => (b.year || 0) - (a.year || 0))
          : [];
        setFestivals(sortedList);
        setActiveFestivalId(active?.id || null);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load festivals for bulk import:', err);
          setError('Failed to load festivals.');
        }
      } finally {
        if (!cancelled) setFestivalsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Load current festival booths once per open so we can auto-propose
  // matching booth assignments and power the "add booth" popover.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setBoothsLoading(true);
      try {
        const response = await api.get('/admin/booths');
        if (cancelled) return;
        setCurrentBooths(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load current festival booths:', err);
          setCurrentBooths([]);
        }
      } finally {
        if (!cancelled) setBoothsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const loadReturning = useCallback(async ({ sourceId, statusList } = {}) => {
    setVendorsLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const params = { statuses: (statusList || DEFAULT_STATUSES).join(',') };
      if (sourceId) params.sourceFestivalId = sourceId;
      const response = await api.get('/admin/vendors/returning', { params });
      const payload = response.data || {};
      const vendorList = Array.isArray(payload.vendors) ? payload.vendors : [];
      setVendors(vendorList);
      setSourceFestival(payload.sourceFestival || null);
      if (payload.sourceFestival && !sourceId) {
        setSourceFestivalId(payload.sourceFestival.id);
      }
      // Reset assignments; they will be recomputed by the effect below once
      // both vendors and booths are loaded.
      setVendorAssignments({});
    } catch (err) {
      console.error('Failed to load returning vendors:', err);
      setError(err.response?.data?.error || 'Failed to load returning vendors.');
      setVendors([]);
      setVendorAssignments({});
    } finally {
      setVendorsLoading(false);
    }
  }, []);

  // Recompute initial assignments whenever the vendor list or booth pool
  // changes. We deliberately reset any in-progress edits here because the
  // underlying inputs are different and edits keyed by vendor id would be
  // stale.
  useEffect(() => {
    if (vendorsLoading || boothsLoading) return;
    if (vendors.length === 0) {
      setVendorAssignments({});
      return;
    }
    setVendorAssignments(computeInitialAssignments(vendors, currentBooths));
  }, [vendors, currentBooths, vendorsLoading, boothsLoading]);

  // Initial load (default source festival) once festivals are ready.
  useEffect(() => {
    if (!open || festivalsLoading) return;
    if (sourceFestivalId) return; // only auto-load on first open
    loadReturning({ sourceId: null, statusList: statuses });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, festivalsLoading]);

  // Reload whenever the user changes source festival or status filter.
  const handleSourceChange = (event) => {
    const value = event.target.value;
    setSourceFestivalId(value);
    loadReturning({ sourceId: value || null, statusList: statuses });
  };

  const toggleStatus = (statusId) => {
    const next = statuses.includes(statusId)
      ? statuses.filter((s) => s !== statusId)
      : [...statuses, statusId];
    setStatuses(next);
    if (next.length > 0) {
      loadReturning({ sourceId: sourceFestivalId || null, statusList: next });
    } else {
      setVendors([]);
    }
  };

  const filteredVendors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter((v) => {
      const haystack = [
        v.business_name,
        v.email,
        v.phone,
        v.firstName,
        v.lastName,
        `${v.firstName || ''} ${v.lastName || ''}`,
        v.description,
        v.website,
        v.address,
        v.city,
        v.state,
        v.zip_code,
        v.category_name,
        v.last_booth_name,
        ...(Array.isArray(v.last_booths) ? v.last_booths.map((b) => b.cell_name) : []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [vendors, search]);

  // Lookup of booth.id -> vendor business_name for booths currently claimed
  // by *other* vendors in this import session, so the map can render them
  // as unavailable with a helpful tooltip.
  const claimedByOtherForVendor = useCallback(
    (activeVendorId) => {
      const map = new Map();
      Object.entries(vendorAssignments).forEach(([vendorIdStr, list]) => {
        const vid = parseInt(vendorIdStr, 10);
        if (vid === activeVendorId) return;
        (list || []).forEach((a) => {
          if (!a.boothId) return;
          const owner = vendors.find((v) => v.id === vid);
          map.set(a.boothId, owner?.business_name || 'another vendor');
        });
      });
      return map;
    },
    [vendorAssignments, vendors]
  );

  const removeBoothFromVendor = (vendorId, index) => {
    setVendorAssignments((prev) => {
      const list = prev[vendorId] || [];
      const next = list.filter((_, i) => i !== index);
      return { ...prev, [vendorId]: next };
    });
  };

  const removeBoothByBoothId = (vendorId, boothId) => {
    setVendorAssignments((prev) => {
      const list = prev[vendorId] || [];
      const next = list.filter((a) => a.boothId !== boothId);
      return { ...prev, [vendorId]: next };
    });
  };

  const addBoothToVendor = (vendorId, booth) => {
    setVendorAssignments((prev) => {
      const list = prev[vendorId] || [];
      if (list.some((a) => a.boothId === booth.id)) return prev;
      if (list.length >= MAX_BOOTHS_PER_VENDOR) return prev;
      const priorCellNames = new Set(
        (vendors.find((v) => v.id === vendorId)?.last_booths || []).map((b) =>
          String(b.cell_name)
        )
      );
      const source = priorCellNames.has(String(booth.cell_name)) ? 'prior' : 'manual';
      return {
        ...prev,
        [vendorId]: [
          ...list,
          {
            boothId: booth.id,
            cellName: booth.cell_name,
            source,
            priorCellName: source === 'prior' ? String(booth.cell_name) : null,
            needsElectricity: false,
            isCorner: Boolean(booth.is_corner),
            hasElectricity: Boolean(booth.has_electricity),
          },
        ],
      };
    });
  };

  const handleMapToggle = (vendorId, cell, shouldSelect) => {
    if (!vendorId || !cell) return;
    if (shouldSelect) addBoothToVendor(vendorId, cell);
    else removeBoothByBoothId(vendorId, cell.id);
  };

  const toggleNeedsElectricity = (vendorId, index) => {
    setVendorAssignments((prev) => {
      const list = prev[vendorId] || [];
      const next = list.map((a, i) =>
        i === index ? { ...a, needsElectricity: !a.needsElectricity } : a
      );
      return { ...prev, [vendorId]: next };
    });
  };

  // Plan summary across all currently *selected* vendors, used in the
  // sticky footer so admins can see at a glance how many booths the import
  // will attempt to assign.
  const planSummary = useMemo(() => {
    let assigned = 0;
    let unmatched = 0;
    selected.forEach((vendorId) => {
      const list = vendorAssignments[vendorId] || [];
      list.forEach((a) => {
        if (a.boothId) assigned += 1;
        else unmatched += 1;
      });
    });
    return { assigned, unmatched };
  }, [selected, vendorAssignments]);

  const allVisibleSelected =
    filteredVendors.length > 0 &&
    filteredVendors.every((v) => selected.has(v.id));
  const someVisibleSelected =
    !allVisibleSelected && filteredVendors.some((v) => selected.has(v.id));

  const toggleVendor = (vendorId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredVendors.forEach((v) => next.add(v.id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const headerCheckboxOnChange = () => {
    if (allVisibleSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredVendors.forEach((v) => next.delete(v.id));
        return next;
      });
    } else {
      selectAllVisible();
    }
  };

  const handleEnroll = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    setResultSummary(null);
    try {
      const assignments = {};
      selected.forEach((vendorId) => {
        const list = vendorAssignments[vendorId] || [];
        const cleaned = list
          .filter((a) => a.boothId)
          .map((a) => ({ boothId: a.boothId, needsElectricity: Boolean(a.needsElectricity) }));
        if (cleaned.length > 0) assignments[vendorId] = cleaned;
      });
      const response = await api.post('/admin/vendors/bulk-enroll', {
        vendorIds: Array.from(selected),
        assignments,
      });
      setResultSummary(response.data);
      // Reload to drop the just-enrolled rows from the candidate list and to
      // refresh booth availability after assignments were made.
      const [, boothsResponse] = await Promise.all([
        loadReturning({
          sourceId: sourceFestivalId || null,
          statusList: statuses,
        }),
        api.get('/admin/booths').catch(() => ({ data: [] })),
      ]);
      setCurrentBooths(Array.isArray(boothsResponse.data) ? boothsResponse.data : []);
    } catch (err) {
      console.error('Bulk enroll failed:', err);
      setError(err.response?.data?.error || 'Failed to enroll vendors.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose(Boolean(resultSummary && resultSummary.enrolled > 0));
  };

  const sourceFestivalLabel = sourceFestival
    ? `${sourceFestival.name} (${sourceFestival.year})`
    : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      TransitionComponent={Transition}
    >
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <GroupAddIcon sx={{ mr: 1 }} color="primary" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Import Returning Vendors
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sourceFestivalLabel
                ? `Pulling from ${sourceFestivalLabel}`
                : 'Pick a prior festival to import from.'}
            </Typography>
          </Box>
          <IconButton edge="end" onClick={handleClose} aria-label="close" disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, pb: 12 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            sx={{ mb: 2 }}
          >
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="source-festival-label">Source festival</InputLabel>
              <Select
                labelId="source-festival-label"
                label="Source festival"
                value={sourceFestivalId || ''}
                onChange={handleSourceChange}
                disabled={festivalsLoading}
              >
                {festivals
                  .filter((f) => f.id !== activeFestivalId)
                  .map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name} ({f.year})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search by name, owner, email, phone, or booth"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1 }}>
              Statuses:
            </Typography>
            {STATUS_OPTIONS.map((opt) => {
              const active = statuses.includes(opt.id);
              return (
                <Chip
                  key={opt.id}
                  label={opt.label}
                  color={active ? 'primary' : 'default'}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={() => toggleStatus(opt.id)}
                  size="small"
                />
              );
            })}
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {resultSummary && (
          <Alert
            severity={
              resultSummary.enrolled > 0
                ? resultSummary.boothFailures?.length > 0
                  ? 'warning'
                  : 'success'
                : 'info'
            }
            sx={{ mb: 2 }}
          >
            {`Enrolled ${resultSummary.enrolled} vendor${resultSummary.enrolled === 1 ? '' : 's'}.`}
            {typeof resultSummary.boothsAssigned === 'number' &&
              ` Assigned ${resultSummary.boothsAssigned} booth${resultSummary.boothsAssigned === 1 ? '' : 's'}.`}
            {resultSummary.skippedAlreadyEnrolled > 0 &&
              ` Skipped ${resultSummary.skippedAlreadyEnrolled} already enrolled.`}
            {resultSummary.notFound > 0 &&
              ` ${resultSummary.notFound} vendor${resultSummary.notFound === 1 ? '' : 's'} not found.`}
            {resultSummary.boothFailures?.length > 0 &&
              ` ${resultSummary.boothFailures.length} booth${resultSummary.boothFailures.length === 1 ? '' : 's'} could not be assigned (taken or unavailable).`}
          </Alert>
        )}

        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someVisibleSelected}
                      checked={allVisibleSelected}
                      onChange={headerCheckboxOnChange}
                      disabled={filteredVendors.length === 0}
                    />
                  </TableCell>
                  <TableCell>Business</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Last Booths</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Assign Booths (current festival)</TableCell>
                  <TableCell>Last Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vendorsLoading || boothsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : filteredVendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {vendors.length === 0
                          ? sourceFestival
                            ? 'No returning vendors match the current filters.'
                            : 'No prior festival data available yet. Pick a source festival once one exists.'
                          : 'No vendors match the search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVendors.map((v) => {
                    const isSelected = selected.has(v.id);
                    const priorBooths = Array.isArray(v.last_booths) ? v.last_booths : [];
                    const proposed = vendorAssignments[v.id] || [];
                    return (
                      <TableRow
                        key={v.id}
                        hover
                        selected={isSelected}
                        onClick={() => toggleVendor(v.id)}
                        sx={{ cursor: 'pointer', verticalAlign: 'top' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleVendor(v.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {v.business_name}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {v.email || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {[v.firstName, v.lastName].filter(Boolean).join(' ') || '—'}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {v.phone || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {priorBooths.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              None
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {priorBooths.map((b, i) => (
                                <Tooltip
                                  key={`${v.id}-prior-${i}-${b.cell_name}`}
                                  title={`From ${v.last_festival_year || 'last festival'}${b.needs_electricity ? ' • electricity' : ''}${b.is_corner ? ' • corner' : ''}`}
                                >
                                  <Chip
                                    size="small"
                                    label={b.cell_name}
                                    variant="outlined"
                                    icon={
                                      b.needs_electricity ? (
                                        <BoltIcon sx={{ fontSize: 14 }} />
                                      ) : undefined
                                    }
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                            {proposed.map((a, i) => {
                              const color =
                                a.source === 'prior'
                                  ? 'success'
                                  : a.source === 'autofill'
                                    ? 'warning'
                                    : a.source === 'manual'
                                      ? 'primary'
                                      : 'error';
                              const tooltipTitle =
                                a.source === 'prior'
                                  ? `Same booth as ${v.last_festival_year || 'last festival'}`
                                  : a.source === 'autofill'
                                    ? `${a.priorCellName} taken — auto-filled with ${a.cellName}`
                                    : a.source === 'manual'
                                      ? 'Manually added'
                                      : `${a.priorCellName} no longer available`;
                              return (
                                <Tooltip
                                  key={`${v.id}-prop-${i}-${a.boothId || a.priorCellName || i}`}
                                  title={tooltipTitle}
                                >
                                  <Chip
                                    size="small"
                                    color={color}
                                    variant={a.source === 'unavailable' ? 'outlined' : 'filled'}
                                    label={
                                      a.boothId
                                        ? a.cellName
                                        : `${a.priorCellName || '?'} (n/a)`
                                    }
                                    icon={
                                      a.needsElectricity ? (
                                        <BoltIcon sx={{ fontSize: 14 }} />
                                      ) : undefined
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (a.hasElectricity) toggleNeedsElectricity(v.id, i);
                                    }}
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      removeBoothFromVendor(v.id, i);
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                            <Tooltip title="Open map to assign booths">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={currentBooths.length === 0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMapVendorId(v.id);
                                  }}
                                >
                                  <MapIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatStatusLabel(v.last_status)}
                            color={
                              v.last_status === 'approved' || v.last_status === 'complete'
                                ? 'success'
                                : v.last_status === 'denied'
                                  ? 'error'
                                  : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, md: 3 },
          py: 1.5,
          gap: 1,
        }}
      >
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>{selected.size}</strong> of {filteredVendors.length} vendors selected
            {filteredVendors.length !== vendors.length ? ` (filtered from ${vendors.length})` : ''}
          </Typography>
          {selected.size > 0 && (
            <Typography variant="body2" color="text.secondary">
              {planSummary.assigned} booth{planSummary.assigned === 1 ? '' : 's'} will be assigned
              {planSummary.unmatched > 0
                ? ` • ${planSummary.unmatched} prior booth${planSummary.unmatched === 1 ? '' : 's'} unmatched`
                : ''}
            </Typography>
          )}
          <Button
            size="small"
            onClick={selectAllVisible}
            disabled={vendorsLoading || filteredVendors.length === 0 || allVisibleSelected}
          >
            Select all visible
          </Button>
          <Button
            size="small"
            onClick={clearSelection}
            disabled={selected.size === 0}
          >
            Clear
          </Button>
        </Box>
        <Button onClick={handleClose} disabled={submitting}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <GroupAddIcon />}
          onClick={handleEnroll}
          disabled={submitting || selected.size === 0}
        >
          {submitting
            ? 'Enrolling...'
            : `Enroll ${selected.size || ''} ${selected.size === 1 ? 'vendor' : 'vendors'}`.trim()}
        </Button>
      </DialogActions>

      {(() => {
        const mapVendor = vendors.find((v) => v.id === mapVendorId) || null;
        const proposed = (mapVendorId && vendorAssignments[mapVendorId]) || [];
        const selectedBoothIds = new Set(
          proposed.map((a) => a.boothId).filter(Boolean)
        );
        const priorCellNames = mapVendor?.last_booths
          ? mapVendor.last_booths.map((b) => b.cell_name)
          : [];
        return (
          <BulkImportBoothMap
            open={Boolean(mapVendorId)}
            onClose={() => setMapVendorId(null)}
            vendor={mapVendor}
            booths={currentBooths}
            claimedByOthers={claimedByOtherForVendor(mapVendorId)}
            selectedBoothIds={selectedBoothIds}
            priorCellNames={priorCellNames}
            maxBooths={MAX_BOOTHS_PER_VENDOR}
            onToggleBooth={(cell, shouldSelect) =>
              handleMapToggle(mapVendorId, cell, shouldSelect)
            }
          />
        );
      })()}
    </Dialog>
  );
};

export default BulkEnrollReturningVendors;
